import { useEffect, useRef, type ChangeEvent } from 'react';
import { useAtom } from 'jotai';
import { useDebounce } from '@/hooks/useDebounce';
import type { AgentApiJobChangeProcessesRecord } from '@/types/serializers';
import JobChangeProcessBoardScreen, { type BoardScreenHandle } from './JobChangeProcessBoardScreen';
import JobChangeProcessBoardSearch from './JobChangeProcessBoardSearch';
import useFetchJobChangeProcessBoardsDetails from '../../api/useFetchJobChangeProcessBoardsDetails';
import useFetchJobChangeProcessBoardsUncategorized from '../../api/useFetchJobChangeProcessBoardsUncategorized';
import usePostJobChangeProcessBoard from '../../api/usePostJobChangeProcessBoard';
import { showErrorToast } from '../../lib/toast';
import { jobChangeProcessSearchAtom } from '../../atoms/jobChangeProcess';

type Props = {
  setScreenType: (value: 'list' | 'board') => void;
  onSelect: (jobChangeProcess: AgentApiJobChangeProcessesRecord) => void;
  selectedId?: number;
};

export default function JobChangeProcessBoardWrapper({
  setScreenType,
  onSelect,
  selectedId,
}: Props) {
  const debounce = useDebounce(500);
  const boardScreenRef = useRef<BoardScreenHandle | null>(null);
  const creatingBoardRef = useRef(false);
  const { mutateAsync: addBoard, isPending: isCreatingBoard } = usePostJobChangeProcessBoard();
  const [boardSearch, setBoardSearch] = useAtom(jobChangeProcessSearchAtom);
  const initialQueryRef = useRef({
    keyword: boardSearch.keyword ?? '',
    organization_user_ids: boardSearch.organizationUserIds ?? [],
  });
  const {
    boards,
    boardsData,
    boardStates,
    boardsRefetch,
    reloadBoards,
    loadMoreBoard,
    setKeyword,
    setOrganizationUserIds,
    isBoardsListFetched,
    filters,
    filterKey,
  } = useFetchJobChangeProcessBoardsDetails(initialQueryRef.current);
  const {
    data: uncategorizedData,
    setKeyword: setUncategorizedKeyword,
    setOrganizationUserIds: setUncategorizedOrganizationUserIds,
    uncategorizedRefetch,
    hasNextPage: uncategorizedHasNextPage,
    isFetching: uncategorizedIsFetching,
  } = useFetchJobChangeProcessBoardsUncategorized(initialQueryRef.current);

  // 無効な担当者IDは Template 側で atom から除かれるため、取得条件をそれに追従させる
  const organizationUserIds = boardSearch.organizationUserIds;
  useEffect(() => {
    if ((filters.organization_user_ids ?? []).join(',') === organizationUserIds.join(',')) return;
    setOrganizationUserIds(organizationUserIds);
    setUncategorizedOrganizationUserIds(organizationUserIds);
  }, [
    organizationUserIds,
    filters.organization_user_ids,
    setOrganizationUserIds,
    setUncategorizedOrganizationUserIds,
  ]);

  // 検索条件の変更はフック側でページを1に戻すため、ここでの明示的な refetch は不要
  const handleKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;
    debounce(() => {
      setKeyword(keyword);
      setUncategorizedKeyword(keyword);
      setBoardSearch((prev) => ({ ...prev, keyword }));
    });
  };

  const handleOrganizationUserIds = (ids: number[]) => {
    setOrganizationUserIds(ids);
    setUncategorizedOrganizationUserIds(ids);
    setBoardSearch((prev) => ({ ...prev, organizationUserIds: ids }));
  };

  const scrollToEndAfterBoardAdded = async () => {
    await new Promise((resolve) => setTimeout(resolve, 300));
    await boardsRefetch();
    boardScreenRef.current?.scrollToEnd();
  };

  // isPending は再レンダリングを待つため連打に間に合わない。ref で同期的にロックする
  const handleCreateBoard = async () => {
    if (creatingBoardRef.current) return;
    creatingBoardRef.current = true;
    try {
      await addBoard({ title: 'Newボード', color_type: 'gray' });
      await scrollToEndAfterBoardAdded();
    } catch (error) {
      showErrorToast(
        error instanceof Error && error.message ? error.message : 'ボードの作成に失敗しました'
      );
    } finally {
      creatingBoardRef.current = false;
    }
  };

  return (
    <div className="flex flex-col gap-xl">
      <JobChangeProcessBoardSearch
        setScreenType={setScreenType}
        keyword={initialQueryRef.current.keyword}
        organizationUserIds={boardSearch.organizationUserIds ?? []}
        handleKeyword={handleKeyword}
        handleOrganizationUserIds={handleOrganizationUserIds}
        onCreateBoard={handleCreateBoard}
        isCreatingBoard={isCreatingBoard}
        boards={boards.records}
        boardsRefetch={boardsRefetch}
      />
      <JobChangeProcessBoardScreen
        ref={boardScreenRef}
        boards={boards.records}
        boardsData={boardsData}
        boardStates={boardStates}
        uncategorized={uncategorizedData}
        boardsRefetch={boardsRefetch}
        reloadBoards={reloadBoards}
        uncategorizedRefetch={uncategorizedRefetch}
        onSelect={onSelect}
        selectedId={selectedId}
        onCreateBoard={handleCreateBoard}
        isCreatingBoard={isCreatingBoard}
        onLoadMoreBoard={loadMoreBoard}
        uncategorizedHasNextPage={uncategorizedHasNextPage}
        uncategorizedIsFetching={uncategorizedIsFetching}
        isLoading={!isBoardsListFetched}
        filterKey={filterKey}
      />
    </div>
  );
}
